<script setup>
import { ref, onMounted, watch } from "vue";
import { motion } from "motion-v";
import apiClient from "@/composables/common/axios";

// 필터 상태 관리 ('day' | 'week' | 'month' | 'all')
const activeFilter = ref('all');
const rankings = ref([]);
const isLoading = ref(false);

// 랭킹 데이터 조회
const fetchRankings = async () => {
  isLoading.value = true;
  try {
    const response = await apiClient.get(`/api/ranking.php?period=${activeFilter.value}&limit=10`);
    if (response.data.success) {
      rankings.value = response.data.rankings;
      // console.log('[Ranking] Loaded:', rankings.value.length, 'rankings');
    }
  } catch (error) {
    console.error('[Ranking] Fetch failed:', error);
    rankings.value = [];
  } finally {
    isLoading.value = false;
  }
};

// 필터 클릭 핸들러
const handleFilterClick = (filter) => {
  activeFilter.value = filter;
};

// 필터 변경 시 데이터 새로고침
watch(activeFilter, () => {
  fetchRankings();
});

// 초기 로드
onMounted(() => {
  fetchRankings();
});

// 숫자 포맷팅
const formatNumber = (num) => {
  return num?.toLocaleString('ko-KR') || '0';
};
</script>
<template>
  <motion.section
    class="ranking-history"
    :initial="{ opacity: 0, y: 20 }"
    :animate="{ opacity: 1, y: 0 }"
    :transition="{
      type: 'spring',
      stiffness: 300,
      damping: 25,
      mass: 0.8,
    }"
  >
    <h2 class="blind">순위</h2>
    <div class="ranking-history__filter">
      <button
        type="button"
        :class="[
          'btn-sm',
          activeFilter === 'all' ? 'btn-primary' : 'btn-ghost'
        ]"
        @click="handleFilterClick('all')"
      >
        전체
      </button>
      <button
        type="button"
        :class="[
          'btn-sm',
          activeFilter === 'day' ? 'btn-primary' : 'btn-ghost'
        ]"
        @click="handleFilterClick('day')"
      >
        일
      </button>
      <button
        type="button"
        :class="[
          'btn-sm',
          activeFilter === 'week' ? 'btn-primary' : 'btn-ghost'
        ]"
        @click="handleFilterClick('week')"
      >
        주
      </button>
      <button
        type="button"
        :class="[
          'btn-sm',
          activeFilter === 'month' ? 'btn-primary' : 'btn-ghost'
        ]"
        @click="handleFilterClick('month')"
      >
        월
      </button>
    </div>
    <div class="table__inner">
      <div class="table__wrap">
        <table class="table table--a">
          <colgroup>
            <col width="400px" />
            <col width="260px" />
            <col width="260px" />
            <col width="260px" />
            <col width="260px" />
            <col width="400px" />
          </colgroup>
          <thead class="table__head">
            <tr class="table__row table__row--th">
              <th class="table__cell table__cell--th">트레이더</th>
              <th class="table__cell table__cell--th">트레이딩</th>
              <th class="table__cell table__cell--th">승리</th>
              <th class="table__cell table__cell--th">승률</th>
              <th class="table__cell table__cell--th">순위</th>
              <th class="table__cell table__cell--th">총 수익</th>
            </tr>
          </thead>
          <tbody class="table__body">
            <!-- 로딩 중 -->
            <tr v-if="isLoading" class="table__row table__row--td">
              <td class="table__cell table__cell--td" colspan="6">
                <div class="table__empty">
                  <span>랭킹 데이터를 불러오는 중...</span>
                </div>
              </td>
            </tr>

            <!-- 데이터 없음 -->
            <tr v-else-if="rankings.length === 0" class="table__row table__row--td">
              <td class="table__cell table__cell--td" colspan="6">
                <div class="table__empty">
                  <span>랭킹 데이터가 없습니다.</span>
                </div>
              </td>
            </tr>

            <!-- 랭킹 목록 -->
            <tr v-else v-for="ranking in rankings" :key="ranking.rank" class="table__row table__row--td">
              <td class="table__cell table__cell--td">
                <div class="table__cell-wrap">
                  <img
                    :src="`/assets/images/default_avatar_${ranking.avatar || '01'}.png`"
                    :alt="`${ranking.name} 프로필`"
                    @error="$event.target.src='/assets/images/default_avatar_01.png'"
                  />
                  <span>{{ ranking.name }}</span>
                </div>
              </td>
              <td class="table__cell table__cell--td">{{ ranking.totalBets }}</td>
              <td class="table__cell table__cell--td">{{ ranking.wins }}</td>
              <td class="table__cell table__cell--td">
                <div class="table__cell-profit">
                  <span>{{ ranking.winRate }}</span>
                  <span>%</span>
                </div>
              </td>
              <td class="table__cell table__cell--td">
                <div class="table__cell-profit">
                  <span>{{ ranking.profitRate }}</span>
                  <span>%</span>
                </div>
              </td>
              <td class="table__cell table__cell--td table__cell--primary">
                <div class="table__cell-profit">
                  <span>₩</span>
                  <span>{{ formatNumber(ranking.profit) }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </motion.section>
</template>
<style scoped></style>
